import {CompanyEntity, getTransportCategory, PersonEntity, TransportCategory} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Input, Modal, ModalProps, Stack} from "@mantine/core";
import {useCallback} from "react";
import {useCompanyTransportServicesReportForm} from "./useCompanyTransportServicesReportForm";
import {DateInput} from "../../date/DateInput/DateInput";
import {OperatorCompanyMultiSelect} from "../../select/entity/CompanySelect/OperatorCompanyMultiSelect";
import {DriverMultiSelect} from "../../select/entity/PersonSelect/DriverMultiSelect";
import {
  TransportCategorySegmentedControl
} from "../../segmented-control/TransportCategorySegmentedControl/TransportCategorySegmentedControl";
import {ReportTimeBasisRadio} from "../../radio/ReportTimeBasisRadio/ReportTimeBasisRadio";

export interface CompanyTransportServicesReportFormProps {
  options?: CompanyTransportServicesReportFormOptions;
  defaultValues?: CompanyTransportServicesReportFormDefaultValues;
  onSubmit?: (values: CompanyTransportServicesReportFormValues) => void;
  submitting?: boolean;
}

export interface CompanyTransportServicesReportFormOptions {
  regionId?: string
  category?: TransportCategory
}

export interface CompanyTransportServicesReportFormValues {
  category?: TransportCategory
  fromDate: Date;
  toDate?: Date;
  reportTimeBasis: string
  operators?: (CompanyEntity | string)[]
  drivers?: (PersonEntity | string)[]
}

export interface CompanyTransportServicesReportFormDefaultValues extends Partial<CompanyTransportServicesReportFormValues> {

}

export interface CompanyTransportServicesReportFormRawValues {
  category?: string;
  fromDate?: Date;
  toDate?: Date;
  reportTimeBasis?: string;
  operators?: string[];
  drivers?: string[]
}

export type CompanyTransportServicesReportFormType = UseFormReturnType<CompanyTransportServicesReportFormRawValues, (values: CompanyTransportServicesReportFormRawValues) => CompanyTransportServicesReportFormRawValues>

export const CompanyTransportServicesReportForm = (props: CompanyTransportServicesReportFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useCompanyTransportServicesReportForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        category: values.category ? getTransportCategory(values.category) : undefined,
        fromDate: values.fromDate!,
        toDate: values.toDate,
        reportTimeBasis: values.reportTimeBasis!,
        operators: values.operators,
        drivers: values.drivers,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("common.report.companytransportservices.form.field.fromdate", "From Date")}
            placeholder={t<string>("common.report.companytransportservices.form.field.fromdate.placeholder", "To Date")}
            clearable
            withAsterisk
            popoverProps={{
              withinPortal: true
            }}
            required
            {...form.getInputProps("fromDate")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            miw={100}
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("common.report.companytransportservices.form.field.todate", "To Date")}
            placeholder={t<string>("common.report.companytransportservices.form.field.todate", "To Date")}
            clearable
            popoverProps={{
              withinPortal: true
            }}
            {...form.getInputProps("toDate")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <ReportTimeBasisRadio
            label={t("common.report.companytransportservices.form.field.timebasis", "Time Basis")}
            withAsterisk
            required
            {...form.getInputProps("reportTimeBasis")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider my="sm" variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={12}>
          <Stack align="flex-start" spacing="xs">
            <Input.Label>Category</Input.Label>
            <TransportCategorySegmentedControl
              includeAll={true}
              {...form.getInputProps("category")}
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider my="sm" variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={12}>
          <OperatorCompanyMultiSelect
            label={t("common.report.companytransportservices.form.field.operator", "Operator")}
            placeholder={t<string>("common.report.companytransportservices.form.field.operator.placeholder", "Operator")}
            selector={{
              regionId: options.regionId
            }}
            clearable
            {...form.getInputProps("operators")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <DriverMultiSelect
            label={t("common.report.companytransportservices.form.field.driver", "Driver")}
            placeholder={t<string>("common.report.companytransportservices.form.field.driver.placeholder", "Driver")}
            selector={{
              regionId: options.regionId
            }}
            clearable
            {...form.getInputProps("drivers")} />
        </Grid.Col>
      </Grid>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface CompanyTransportServicesReportFormModalProps extends CompanyTransportServicesReportFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const CompanyTransportServicesReportFormModal = (props: CompanyTransportServicesReportFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <CompanyTransportServicesReportForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
